/**
 * Smart Content Risk Alerts (§22.12).
 *
 * A calm digest of what the content filter blocked, grouped by *kind* of
 * risk, with a "what to do next" line. Deliberately never shows the
 * verbatim URL/content — it's guidance for the parent, not surveillance.
 * Hidden entirely when there's nothing to flag.
 */
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert, ShieldX, ShieldQuestion } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { analyticsApi, type RiskSeverity } from '@/api/analytics';

const SEVERITY_STYLE: Record<
  RiskSeverity,
  { chip: string; border: string; Icon: typeof ShieldAlert }
> = {
  critical: { chip: 'bg-rose-100 text-rose-800', border: 'border-rose-200', Icon: ShieldX },
  high: { chip: 'bg-amber-100 text-amber-900', border: 'border-amber-200', Icon: ShieldAlert },
  medium: { chip: 'bg-sky-100 text-sky-800', border: 'border-sky-200', Icon: ShieldQuestion },
  low: { chip: 'bg-slate-100 text-slate-700', border: 'border-slate-200', Icon: ShieldQuestion },
};

export function RiskAlertsCard() {
  const alerts = useQuery({
    queryKey: ['risk-alerts'],
    queryFn: () => analyticsApi.riskAlerts(7),
    refetchInterval: 120_000,
    retry: false,
  });

  const items = alerts.data ?? [];
  if (items.length === 0) return null;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-baseline justify-between">
          <p className="text-sm font-semibold">Content risk alerts</p>
          <span className="text-xs text-[color:var(--kz-color-ink-muted)]">last 7 days</span>
        </div>
        <div className="mt-3 grid gap-2">
          {items.map((a) => {
            const style = SEVERITY_STYLE[a.severity];
            return (
              <div
                key={a.category}
                className={cn('rounded-card border bg-white/60 p-3', style.border)}
              >
                <div className="flex items-center gap-2">
                  <span className={cn('grid size-7 shrink-0 place-items-center rounded-full', style.chip)}>
                    <style.Icon className="size-4" />
                  </span>
                  <p className="flex-1 text-sm font-medium">{a.label}</p>
                  <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium', style.chip)}>
                    {a.count} blocked
                  </span>
                </div>
                <p className="mt-1.5 pl-9 text-xs text-[color:var(--kz-color-ink-secondary)]">
                  {a.advice}
                </p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
